/**
 * Centralized Facebook Pixel event firing.
 * Use this module instead of calling fbq() directly for consistency and easier maintenance.
 *
 * The fbq function is loaded by _analytics.html.haml on page load.
 * In development/test, fbq may be a no-op that logs to console.
 *
 * @example
 *   import { trackFacebookCustomEvent } from '../support/facebook_events'
 *   trackFacebookCustomEvent('demo_scheduled')
 *   trackFacebookCustomEvent('lead', { content_name: 'demo_form' })
 */

const ATTRIBUTION_STORAGE_KEYS = ["healmeUtmParams", "utmParams"]
const FBC_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60

/**
 * Fire a Facebook custom event (trackCustom).
 * Safe to call even if fbq is not yet loaded - the call will be queued by the pixel.
 *
 * @param {string} eventName - Custom event name (e.g. 'demo_scheduled', 'typeform_opened')
 * @param {Object} [eventData={}] - Optional event parameters
 * @param {Object} [options={}] - Optional { eventID } for deduplication with server-side CAPI
 */
export function trackFacebookCustomEvent(eventName, eventData = {}, options = {}) {
  if (typeof window === "undefined") return

  const fbq = window.fbq
  if (typeof fbq !== "function") {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log("[facebook_events] fbq not available, would have fired:", eventName, eventData, options)
    }
    return
  }

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log("[facebook_events] Firing trackCustom:", eventName)
  }
  fbq("trackCustom", eventName, eventData, options)
}

/**
 * Fire a standard Facebook event (track).
 * Use for built-in events: PageView, ViewContent, Lead, Purchase, etc.
 *
 * @param {string} eventName - Standard event name
 * @param {Object} [eventData={}] - Optional event parameters
 * @param {Object} [options={}] - Optional { eventID } for deduplication
 */
export function trackFacebookEvent(eventName, eventData = {}, options = {}) {
  if (typeof window === "undefined") return

  const fbq = window.fbq
  if (typeof fbq !== "function") {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log("[facebook_events] fbq not available, would have fired:", eventName, eventData, options)
    }
    return
  }

  fbq("track", eventName, eventData, options)
}

function readCookie(name) {
  if (typeof document === "undefined") return null

  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
  return match ? decodeURIComponent(match[2]) : null
}

function writeCookie(name, value) {
  if (typeof document === "undefined" || !value) return

  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; path=/; max-age=${FBC_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
}

function readUrlParam(name) {
  if (typeof window === "undefined") return null

  try {
    return new URLSearchParams(window.location.search).get(name) || null
  } catch {
    return null
  }
}

function getStoredAttributionParams() {
  if (typeof window === "undefined") return {}

  const storedParams = {}

  ATTRIBUTION_STORAGE_KEYS.forEach((storageKey) => {
    try {
      const storedValue = window.localStorage.getItem(storageKey)
      if (!storedValue) return

      const parsedValue = JSON.parse(storedValue)
      if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) return

      Object.entries(parsedValue).forEach(([key, value]) => {
        if (typeof value === "string" && !storedParams[key]) {
          storedParams[key] = value
        }
      })
    } catch {
      // Ignore storage access issues and malformed values.
    }
  })

  return storedParams
}

function readAttributionParam(name) {
  const urlValue = readUrlParam(name)
  if (urlValue) return urlValue

  const storedParams = getStoredAttributionParams()
  return storedParams[name] || null
}

function buildFbcFromFbclid(fbclid) {
  if (!fbclid) return null

  return `fb.1.${Date.now()}.${fbclid}`
}

function getMetaMatchKeys() {
  const fbp = readCookie("_fbp")
  const cookieFbc = readCookie("_fbc")
  const fbc = cookieFbc || buildFbcFromFbclid(readAttributionParam("fbclid"))

  if (!cookieFbc && fbc) {
    writeCookie("_fbc", fbc)
  }

  return {
    fbp,
    fbc,
    fbLoginId: readAttributionParam("fb_login_id")
  }
}

function sendTrackingPayload(payload, trackingUrl) {
  if (typeof window === "undefined") return

  const body = JSON.stringify(payload)

  if (navigator.sendBeacon && typeof navigator.sendBeacon === "function") {
    try {
      const blob = new Blob([body], { type: "application/json" })
      if (navigator.sendBeacon(trackingUrl, blob)) return
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("[facebook_events] sendBeacon failed, falling back to fetch:", error)
      }
    }
  }

  fetch(trackingUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body,
    keepalive: true
  }).catch((error) => {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("[facebook_events] Tracking request failed:", error)
    }
  })
}

export function sendFacebookServerEvent({
  kind,
  eventName,
  eventId,
  eventSourceUrl,
  email,
  firstName,
  lastName,
  phoneNumber,
  trackingUrl = "/api/tracking"
}) {
  if (typeof window === "undefined" || !eventId || !eventName || !kind) return

  const metaMatchKeys = getMetaMatchKeys()

  sendTrackingPayload(
    {
      kind,
      event_name: eventName,
      event_id: eventId,
      event_source_url: eventSourceUrl || window.location.href,
      email,
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
      fbp: metaMatchKeys.fbp,
      fbc: metaMatchKeys.fbc,
      fb_login_id: metaMatchKeys.fbLoginId
    },
    trackingUrl
  )
}

export function trackFacebookEventWithServer(eventName, eventData = {}, options = {}, serverData = {}) {
  trackFacebookEvent(eventName, eventData, options)
  sendFacebookServerEvent({
    kind: "standard_event",
    eventName,
    eventId: options.eventID,
    ...serverData
  })
}

export function trackFacebookCustomEventWithServer(eventName, eventData = {}, options = {}, serverData = {}) {
  trackFacebookCustomEvent(eventName, eventData, options)
  sendFacebookServerEvent({
    kind: "custom_event",
    eventName,
    eventId: options.eventID,
    ...serverData
  })
}
